import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, FileText } from "lucide-react";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { useLanguage } from "@/hooks/use-language";

// Terms of Service Page (EULA - Full Content)
const TermsOfServicePage = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const hideHeader = searchParams.get("header") === "false";
  const hideFooter = searchParams.get("footer") === "false";

  return (
    <div className="bg-[#030014] min-h-screen overflow-x-hidden">
      {!hideHeader && <Header />}
      <main className={hideHeader ? "pt-8 pb-20" : "pt-24 pb-20"}>
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16">
          {/* Back Button */}
          {!hideHeader && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Link
                to="/policy"
                className="inline-flex items-center gap-2 text-white/60 hover:text-[#FF2E63] transition-colors mb-8"
                data-testid="back-policy-btn"
              >
                <ArrowLeft className="w-5 h-5" />
                {t("nav.backToPolicies")}
              </Link>
            </motion.div>
          )}

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF2E63]/20 to-pink-600/10 flex items-center justify-center">
                <FileText className="w-7 h-7 text-[#FF2E63]" />
              </div>
              <div>
                <p className="text-[#FF2E63] text-xs tracking-[0.3em] uppercase">
                  {t("policy.legal")}
                </p>
                <h1 className="font-display font-bold text-3xl sm:text-4xl text-white">
                  {t("policy.termsOfService")}
                </h1>
              </div>
            </div>
            <p className="text-white/50">{t("policy.lastUpdated")}</p>
          </motion.div>

          {/* Important Notice */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-10 p-6 rounded-2xl bg-[#FF2E63]/10 border border-[#FF2E63]/30"
          >
            <p className="text-white font-semibold text-lg">
              <strong>IMPORTANT:</strong> PLEASE READ THESE TERMS CAREFULLY. BY
              ACCESSING OR USING THE APP, YOU AGREE TO BE BOUND BY THESE TERMS.
              IF YOU DO NOT AGREE, DO NOT USE THE APP.
            </p>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-10 text-white/80 leading-relaxed"
          >
            {/* Section 1 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                1. Eligibility
              </h2>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  You must meet the{" "}
                  <strong>
                    minimum age required by the applicable app store
                    (distribution platform) and applicable law
                  </strong>{" "}
                  to access or use PHUONG. By using the App, you confirm that you
                  meet this requirement.
                </li>
              </ul>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                2. User Accounts
              </h2>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  <strong>Security:</strong> You are responsible for maintaining
                  the confidentiality of your account credentials and agree not
                  to share your account with anyone.
                </li>
                <li>
                  <strong>Accuracy:</strong> You agree to provide accurate
                  information when registering and to keep it up to date.
                </li>
                <li>
                  <strong>Termination:</strong> We reserve the right to suspend
                  or permanently terminate your account immediately, without
                  prior notice or refund, if you violate these Terms.
                </li>
              </ul>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                3. Virtual Currency, Purchases &amp; Refund Policy
              </h2>

              <h3 className="text-xl font-semibold text-white mt-6 mb-3">
                A. PCoin
              </h3>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>
                  <strong>What it is:</strong> PCoin is PHUONG's in-app currency
                  that can be purchased with real money and used to access
                  features, purchase models, and unlock content.
                </li>
                <li>
                  <strong>In-App Use Only:</strong> PCoin has no real-world
                  monetary value and{" "}
                  <strong>
                    cannot be exchanged, redeemed, or converted into cash,
                    cryptocurrency, or any other real-world currency
                  </strong>
                  . It is valid only within the PHUONG ecosystem.
                </li>
                <li>
                  <strong>Creator Earnings:</strong> Creators who sell models may
                  earn PCoin. This PCoin{" "}
                  <strong>cannot be withdrawn as cash</strong>; it can only be
                  used within the platform to purchase other models or be
                  converted into other in-app items (such as Bluebreads).
                </li>
                <li>
                  <strong>Limits:</strong> Web deposit limits and mobile pricing
                  follow the configuration set by PHUONG and by the Apple App
                  Store or Google Play Store.
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-white mt-6 mb-3">
                B. Bluebreads
              </h3>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>
                  <strong>What it is:</strong> Bluebreads are an in-app energy
                  unit used to power interactions with your PHUONG AI companion
                  and to purchase certain content (such as paid songs on
                  PHUNMUSIC).
                </li>
                <li>
                  <strong>In-App Use Only:</strong> Bluebreads have no real-world
                  monetary value, cannot be converted into cash, and are
                  non-transferable between accounts.
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-white mt-6 mb-3">
                C. Sunrise &amp; Reward Items
              </h3>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>
                  Sunrise and reward units (where present) are in-app virtual
                  benefits, obtainable through purchase or rewarded ads where
                  available. They have no real-world value and cannot be
                  converted into cash.
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-white mt-6 mb-3">
                D. No Refund Policy
              </h3>
              <div className="p-6 rounded-2xl bg-[#FF2E63]/10 border border-[#FF2E63]/30 mb-4">
                <p className="text-white font-semibold mb-3">
                  All purchases made within PHUONG are final, to the fullest
                  extent permitted by applicable law. We do not provide refunds
                  for:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4 text-white/80">
                  <li>PCoin deposits made via web payment processors</li>
                  <li>Model purchases on the marketplace</li>
                  <li>Membership or subscription fees</li>
                  <li>Tips sent to creators</li>
                  <li>Bluebreads</li>
                  <li>Paid content on PHUNMUSIC</li>
                  <li>Any other in-app transaction or purchase</li>
                </ul>
              </div>
              <p>
                <strong>Exception:</strong> Mobile in-app purchases made through
                the Apple App Store or Google Play Store are governed by the
                respective store's refund policies; please contact Apple or
                Google directly. This no-refund policy is not intended to exclude
                consumer rights that cannot be waived under the mandatory law of
                your jurisdiction.
              </p>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                4. User Content &amp; Creator Marketplace
              </h2>

              <h3 className="text-xl font-semibold text-white mt-6 mb-3">
                A. PHUNSOCIAL Content
              </h3>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>
                  You retain ownership of the original content (posts, images,
                  videos) you upload to PHUNSOCIAL.
                </li>
                <li>
                  By posting content, you grant PHUONG a worldwide,
                  royalty-free, non-exclusive license to host, display, and
                  distribute your content within the platform.
                </li>
                <li>
                  We reserve the right to remove any content that violates our
                  Community Standards, without notice or compensation.
                </li>
                <li>
                  Content you delete will be removed from public view. Residual
                  data may be retained for audit and legal compliance.
                </li>
              </ul>

              <div className="p-5 rounded-xl bg-white/5 border border-white/10 mb-4">
                <h4 className="text-white font-semibold mb-2">
                  Your Copyright Responsibility
                </h4>
                <p className="text-white/70 text-sm mb-3">
                  By posting any content on PHUNSOCIAL, you represent and warrant
                  that:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4 text-white/70 text-sm">
                  <li>
                    You are the original creator, or have obtained all necessary
                    rights, licenses, and permissions to post and distribute it.
                  </li>
                  <li>
                    The content does not infringe any third-party copyright,
                    trademark, or other intellectual property rights.
                  </li>
                  <li>
                    If your content includes music, audio, video, or images
                    created by third parties, you hold the appropriate license or
                    explicit authorization.
                  </li>
                </ul>
              </div>

              <div className="p-5 rounded-xl bg-[#FF2E63]/10 border border-[#FF2E63]/30 mb-4">
                <h4 className="text-white font-semibold mb-2">
                  Prohibited: Re-posting Others' Content
                </h4>
                <p className="text-white/70 text-sm">
                  You agree not to re-post, re-upload, or distribute content
                  originally created by other users or third parties without the
                  explicit written permission of the original rights holder.
                </p>
              </div>

              <div className="p-5 rounded-xl bg-white/5 border border-white/10 mb-4">
                <h4 className="text-white font-semibold mb-2">
                  PHUONG as Platform Host
                </h4>
                <p className="text-white/70 text-sm">
                  PHUONG operates as a hosting platform for user-generated
                  content. We are not the author, creator, editor, or publisher
                  of content posted by users, and assume no liability for
                  user-submitted content that may infringe third-party rights.
                  Liability for such content rests solely with the user who
                  posted it. You agree to indemnify and hold PHUONG harmless from
                  any claims arising from your violation of third-party rights.
                </p>
              </div>

              <h3 className="text-xl font-semibold text-white mt-6 mb-3">
                B. Creator Marketplace (AI Models)
              </h3>
              <div className="space-y-4">
                <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                  <h4 className="text-white font-semibold mb-2">
                    License &amp; Creator Rights
                  </h4>
                  <p className="text-white/70 text-sm">
                    By uploading a model, you retain ownership of your original
                    content but grant PHUONG a worldwide, royalty-free license to
                    host, display, distribute, and operate the model in
                    connection with the platform; this license survives
                    termination to the extent needed to operate. While your
                    account is in good standing, you may edit, update,
                    re-configure, or delete your model.
                  </p>
                </div>

                <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                  <h4 className="text-white font-semibold mb-2">
                    Representations &amp; Warranties
                  </h4>
                  <p className="text-white/70 text-sm">
                    You represent and warrant that you own or have obtained all
                    necessary rights to your content and that it does not
                    infringe any third party's rights. You agree to indemnify
                    PHUONG against any claims arising from a breach of this
                    warranty.
                  </p>
                </div>

                <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                  <h4 className="text-white font-semibold mb-2">
                    Distribution Tiers
                  </h4>
                  <ul className="list-disc list-inside space-y-1 ml-4 text-white/70 text-sm">
                    <li>
                      <strong>Free:</strong> Any authenticated user may download
                      and use the model.
                    </li>
                    <li>
                      <strong>Paid:</strong> Access requires sufficient PCoin; you
                      set the price.
                    </li>
                    <li>
                      <strong>Private:</strong> Visible and usable only by you.
                    </li>
                  </ul>
                </div>

                <div className="p-5 rounded-xl bg-red-500/10 border border-red-500/30">
                  <h4 className="text-white font-semibold mb-2">
                    Administrative Actions
                  </h4>
                  <p className="text-white/70 text-sm">
                    PHUONG may disable any model indefinitely, without prior
                    notice or compensation, if it violates these Terms or our
                    Community Standards, and may permanently ban any user for
                    serious or repeated violations. Disabled models and banned
                    accounts are not eligible for refunds.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                5. PHUNMUSIC (Music)
              </h2>
              <p className="mb-4">
                This section applies specifically to the PHUNMUSIC music
                platform, in addition to the general terms above.
              </p>
              <div className="p-5 rounded-xl bg-[#FF2E63]/10 border border-[#FF2E63]/30 mb-4">
                <h4 className="text-white font-semibold mb-2">
                  Listener Rights
                </h4>
                <p className="text-white/70 text-sm">
                  You may only <strong>stream</strong> music within the App.{" "}
                  <strong>
                    Downloading, copying, recording, redistributing,
                    re-publishing, reselling, or commercially exploiting
                  </strong>{" "}
                  any track, lyrics, or musical content in any form is strictly
                  prohibited. Purchasing a paid song grants you only the{" "}
                  <strong>right to listen</strong> within the platform and does
                  not transfer ownership or any reproduction rights.
                </p>
              </div>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>
                  <strong>Content Copyright:</strong> Music on PHUNMUSIC belongs
                  to and is protected for its rights holders; you may not use it
                  beyond personal in-app listening.
                </li>
                <li>
                  <strong>Uploading Music:</strong> Uploading music is limited to
                  creators authorized by the platform. If PHUONG opens uploading
                  to other creators, each creator is{" "}
                  <strong>
                    solely responsible for, and must prove, ownership or
                    licensing
                  </strong>{" "}
                  of the recording, composition, lyrics, and cover art they
                  upload. A creator providing copyright evidence{" "}
                  <strong>
                    does not mean PHUONG has verified or validated the legality
                  </strong>{" "}
                  of those rights.
                </li>
                <li>
                  <strong>Takedown &amp; Repeat Infringers:</strong> PHUONG may
                  remove or disable any musical content upon a valid rights-holder
                  complaint or suspected infringement, without prior notice, and
                  applies a repeat-infringer termination policy (Section 6).
                </li>
                <li>
                  <strong>Lyrics &amp; Translations</strong> are creator/user
                  content and subject to the same IP, warranty, and moderation
                  rules.
                </li>
              </ul>
              <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                <p className="text-white/70 text-sm">
                  Playback access may be technically restricted and monitored to
                  protect copyright and prevent abuse. We do not guarantee
                  download capability or offline ownership of any content.
                </p>
              </div>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                6. Intellectual Property &amp; Copyright Complaints
              </h2>
              <p className="mb-4">
                The Services contain content, features, and technology protected
                by copyright, trademark, and other rights. You agree not to copy,
                scrape, rent, sell, distribute, or create derivative works from
                the Services, except for your own User Content that you legally
                upload. The PHUONG name, logos, and branding are trademarks of
                PHUONG.
              </p>
              <p className="mb-4">
                PHUONG acts as a passive hosting intermediary for user-generated
                content and does not exercise editorial control or proactively
                monitor for copyright compliance. Upon receipt of a valid
                takedown notice, we will act promptly to remove infringing
                content, consistent with applicable safe-harbor provisions
                (including the DMCA in the United States and equivalent laws
                elsewhere).
              </p>
              <div className="p-5 rounded-xl bg-white/5 border border-white/10 mb-4">
                <h4 className="text-white font-semibold mb-2">
                  Filing a Copyright Complaint
                </h4>
                <p className="text-white/70 text-sm">
                  Send a written notice to{" "}
                  <a
                    href="mailto:contact@phuongai.com"
                    className="text-[#FF2E63] hover:underline"
                  >
                    contact@phuongai.com
                  </a>{" "}
                  (Subject: "DMCA Takedown Request") including: a signature of an
                  authorized person; a description of the infringed work; the
                  location of the infringing material; your contact information;
                  a good-faith statement that the use is unauthorized; and a
                  statement, under penalty of perjury, that the information is
                  accurate and you are the rights holder or authorized agent. A
                  user whose content is removed may submit a counter-notice to
                  the same address. PHUONG terminates repeat infringers in
                  appropriate circumstances.
                </p>
              </div>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                7. Prohibited Conduct (Zero Tolerance)
              </h2>
              <p className="mb-4">
                You agree <strong>NOT</strong> to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-6">
                <li>
                  Use PHUONG for any purpose illegal under Vietnamese or
                  applicable international law.
                </li>
                <li>
                  Hack, reverse engineer, probe for vulnerabilities, or
                  compromise the integrity, security, or availability of our
                  systems; or conduct any cyberattack or unauthorized access.
                </li>
                <li>
                  Create or distribute content that opposes, slanders, or incites
                  action against the Socialist Republic of Vietnam, its
                  government, laws, or national security.
                </li>
                <li>
                  Engage in fraud, scams, phishing, or deceptive conduct to
                  extract money, data, or access from others.
                </li>
                <li>
                  Post hate speech or content inciting discrimination or violence
                  based on protected characteristics; harass, bully, threaten, or
                  dox others.
                </li>
                <li>
                  Create, upload, distribute, or possess Child Sexual Abuse
                  Material (CSAM) — which will be reported immediately to NCMEC
                  and law enforcement.
                </li>
                <li>
                  Impersonate users, creators, PHUONG staff, or any organization.
                </li>
                <li>
                  Use bots, scripts, or scrapers to access or manipulate the
                  platform; spam or engage in coordinated inauthentic behavior.
                </li>
                <li>
                  Attempt to bypass PHUONG's AI safety measures to produce
                  harmful, illegal, or policy-violating content.
                </li>
              </ul>
              <div className="p-6 rounded-2xl bg-red-500/10 border border-red-500/30">
                <p className="text-white font-semibold">
                  Violation of this section may result in an immediate permanent
                  ban and potential civil or criminal legal action. PHUONG
                  cooperates fully with law enforcement.
                </p>
              </div>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                8. Subscription Plans
              </h2>
              <p className="mb-4">
                PHUONG currently offers a <strong>Free</strong> plan (core
                features with limits) and a <strong>Pro</strong> plan (advanced
                AI capabilities). Subscriptions on mobile are managed and billed
                by the Apple App Store or Google Play Store.
              </p>
              <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                <p className="text-white/80 text-sm">
                  <strong>Important:</strong> If your account is suspended or
                  banned, you are solely responsible for canceling any active
                  recurring subscription through the Apple or Google store
                  settings. PHUONG cannot cancel mobile subscriptions on your
                  behalf, and no refunds are issued for any remaining period.
                </p>
              </div>
            </section>

            {/* Section 9 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                9. Advertising &amp; Rewarded Ads
              </h2>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  The App may display advertising, including rewarded ads.
                  Watching a rewarded ad may grant in-app virtual items.
                </li>
                <li>
                  We do not guarantee that ads are always available and may
                  change or discontinue this feature.
                </li>
                <li>
                  Rewards are granted when an ad is validly verified; rewards
                  obtained through fraud, abuse, or error may be revoked or
                  adjusted.
                </li>
                <li>
                  How advertising data is handled is described in our Privacy
                  Policy (Section 4).
                </li>
              </ul>
            </section>

            {/* Section 10 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                10. Disclaimers
              </h2>
              <p className="mb-4">
                The App is provided on an "AS IS" and "AS AVAILABLE" basis. We
                disclaim all warranties, express or implied.
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>
                  <strong>AI:</strong> PHUONG characters are powered by Large
                  Language Models and may produce inaccurate, incomplete, or
                  "hallucinated" information. They are fictional entities; their
                  statements are not professional, legal, medical, or financial
                  advice.
                </li>
                <li>
                  <strong>Medical &amp; Mental Health:</strong> PHUONG is NOT a
                  healthcare provider, mental health service, crisis hotline, or
                  emergency service. In an emergency, discontinue use and contact
                  your local emergency services immediately.
                </li>
                <li>
                  <strong>Financial (PHUNMONEY):</strong> for personal tracking
                  and informational purposes only — not professional financial
                  advice. We are not liable for financial losses or decisions
                  arising from your reliance on the App.
                </li>
                <li>
                  <strong>Service Availability:</strong> we do not guarantee
                  uninterrupted access; the service may be unavailable due to
                  maintenance, technical issues, or factors beyond our control.
                </li>
              </ul>
            </section>

            {/* Section 11 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                11. Data Usage &amp; AI Memory
              </h2>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  <strong>No Training on Your Data:</strong> PHUONG does not use
                  your conversations, messages, uploads, financial entries,
                  social posts, or music content to train or fine-tune any models
                  of PHUONG or third parties, unless you separately opt in in the
                  future. When data is sent to AI/TTS providers to deliver the
                  service, we configure routing to providers that commit not to
                  retain or train on the data, to the extent within our control.
                </li>
                <li>
                  <strong>AI Memory</strong> is specific to your relationship with
                  each companion, is not shared across users, and is not used for
                  any other purpose.
                </li>
                <li>
                  For full details, see our{" "}
                  <Link
                    to="/policy/privacy"
                    className="text-[#FF2E63] hover:underline"
                  >
                    Privacy Policy
                  </Link>
                  .
                </li>
              </ul>
            </section>

            {/* Section 12 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                12. Indemnification
              </h2>
              <p className="mb-4">
                You agree to release, indemnify, and hold harmless PHUONG, its
                developer, affiliates, officers, employees, and agents from and
                against any and all losses, damages, expenses (including
                reasonable attorneys' fees), claims, and actions arising out of:
                your use or misuse of the Services; any User Content or models you
                create, upload, or distribute; your violation of these Terms; or
                your violation of any third-party rights. This obligation
                survives the termination of your account and these Terms.
              </p>
            </section>

            {/* Section 13 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                13. Limitation of Liability
              </h2>
              <div className="p-6 rounded-2xl bg-white/5 border border-white/10 mb-4">
                <p className="text-white/80 text-sm uppercase font-semibold">
                  TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, PHUONG AND
                  ITS DEVELOPER SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
                  SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES,
                  INCLUDING LOSS OF PROFITS, GOODWILL, USE, OR DATA, RESULTING
                  FROM YOUR USE OF OR INABILITY TO USE THE SERVICES.
                </p>
              </div>
              <p className="mb-4">
                In no event shall our total liability to you exceed the greater
                of: (a) the total amount you paid to us in the 6 months preceding
                the claim, or (b) $50 USD.
              </p>
              <p className="text-white/60 text-sm">
                <strong className="text-white">Exceptions:</strong> Nothing in
                these Terms excludes or limits PHUONG's liability for death or
                personal injury resulting from negligence, fraud, or any other
                matters that cannot be excluded or limited under applicable law.
              </p>
            </section>

            {/* Section 14 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                14. Account Termination &amp; Consequences
              </h2>
              <p className="mb-4">Upon account ban or termination:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>
                  All access to PHUONG services is immediately and permanently
                  revoked.
                </li>
                <li>
                  Your PCoin balance is forfeited in its entirety and cannot be
                  recovered, refunded, or transferred.
                </li>
                <li>
                  Models you have created remain subject to the license granted
                  to PHUONG.
                </li>
                <li>
                  You are solely responsible for canceling recurring
                  subscriptions via Apple or Google.
                </li>
                <li>
                  No refunds will be issued for any purchases, deposits, or unused
                  balances.
                </li>
              </ul>
            </section>

            {/* Section 15 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                15. Governing Law
              </h2>
              <p className="mb-4">
                These Terms are governed by the laws of the Socialist Republic of
                Vietnam, without regard to conflict-of-law provisions.
              </p>
              <p>
                For users in the EEA or other jurisdictions with specific data
                protection laws (including GDPR), your additional rights are
                addressed in our Privacy Policy.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Contact</h2>
              <p className="mb-4">
                For questions about these Terms of Service, please contact us at:
              </p>
              <div className="p-6 rounded-2xl bg-white/5 border border-white/10">
                <p className="text-white/60 mb-2">Email:</p>
                <a
                  href="mailto:contact@phuongai.com"
                  className="text-[#FF2E63] hover:underline text-lg font-medium"
                >
                  contact@phuongai.com
                </a>
              </div>
            </section>
          </motion.div>
        </div>
      </main>
      {!hideFooter && <Footer />}
    </div>
  );
};

export default TermsOfServicePage;
